import { getAuth0AccessToken, logoutAuth0IfSessionActive } from "@/lib/auth0-token";

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

/** Thrown by {@link http} for non-2xx responses. Extends Error so existing
 *  `instanceof Error` / `err.message` consumers keep working. */
export class HttpError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "HttpError";
    this.status = status;
  }
}

// Use empty string in dev (proxy handles /api), full URL in production
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

/** Sends an authenticated request and throws {@link HttpError} for non-2xx responses. */
async function authorizedFetch(
  path: string,
  options: RequestInit = {},
  signal?: AbortSignal
): Promise<Response> {
  const token = await getAuth0AccessToken();
  const isFormData = options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
    signal,
  });

  if (!res.ok) {
    if (res.status === 401) {
      logoutAuth0IfSessionActive();
    }

    let message = `HTTP ${res.status}`;
    try {
      const text = await res.text();
      if (text) message = text;
    } catch {
      // ignore
    }
    throw new HttpError(res.status, message);
  }
  return res;
}

export async function http<T>(
  path: string,
  options: RequestInit = {},
  signal?: AbortSignal
): Promise<T> {
  const res = await authorizedFetch(path, options, signal);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Like {@link http}, for endpoints that return a file rather than JSON. */
export async function httpBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const res = await authorizedFetch(path, {}, signal);
  return res.blob();
}
